import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import qualityManagementService from '../services/qualityManagement.service';

interface Props {
  open: boolean;
  review: {
    id: number;
    batch_number: string;
    no_issue_remarks?: string | null;
  } | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditNoIssueDialog: React.FC<Props> = ({ open, review, onClose, onSaved }) => {
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && review) {
      setRemarks(review.no_issue_remarks || '');
      setError(null);
    }
  }, [open, review]);

  const handleSave = async () => {
    if (!review) return;
    setSaving(true);
    setError(null);

    try {
      await qualityManagementService.submitNoIssue(review.id, remarks);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update no-issue remarks');
    } finally {
      setSaving(false);
    }
  };

  if (!review) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#2E7D32', color: 'white', pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          Edit No Issue Remarks
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          {review.batch_number}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          This review was marked as <strong>No Issue</strong> — no underlying problem requiring corrective action.
        </Alert>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Add any relevant notes about why no issue was found..."
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditNoIssueDialog;
